'use client';

import CustomButton from '@/components/custom-button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { useAuthenticationStore } from '@/provider/customs/useAuthenticationStore';
import { PSCCheckESurvey } from '@/server/api/v1/psc-e-survey/psc-e-survey.action';
import { PSCCheckESurveyResponse } from '@/server/api/v1/psc-e-survey/type';
import { useESurveyStore } from '@/hooks/use-esurvey-store';
import { FileChartPie } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

type UserSurveyType = 'teacher' | 'student' | 'visitor';

/**
 * Tự động kiểm tra và hiển thị khảo sát ESurvey sau khi đăng nhập.
 *
 * Nghiệp vụ:
 * - Không có dữ liệu khảo sát → không hiển thị gì.
 * - Sinh viên / Cán bộ: có khảo sát chưa làm → modal bắt buộc, chặn toàn bộ chức năng.
 *   Mỗi lần người dùng quay lại tab sau khi mở link khảo sát → tự động kiểm tra lại.
 *   Danh sách trả về rỗng (đã làm hết) → đóng modal, cho phép dùng bình thường.
 * - Khách vãng lai: có khảo sát → popup gợi ý (không bắt buộc, đóng được, 1 lần/session).
 */
export const CheckSurveyOnline = memo(() => {
    const transDef = useTranslations('System');
    const authData = useAuthenticationStore((x) => x.auth);

    /**
     * Cập nhật store toàn cục sau mỗi lần fetch thành công.
     * LeftSidebar đọc pendingCount từ store này để hiển thị badge.
     */
    const setSurveyData = useESurveyStore((x) => x.setSurveyData);

    const [isShowModal, setIsShowModal] = useState(false);
    const [userType, setUserType] = useState<UserSurveyType>('visitor');
    const [surveyList, setSurveyList] = useState<PSCCheckESurveyResponse[]>([]);

    // Lưu thông tin để refetch khi người dùng quay lại tab
    const userRef = useRef<{ type: UserSurveyType; id?: string } | null>(null);
    // Chống double-call trong React StrictMode
    const fetchedForRef = useRef<string | null>(null);

    const fetchSurvey = useCallback(
        async (type: UserSurveyType, id?: string) => {
            if (authData) {
                const result = await PSCCheckESurvey({
                    auth: authData,
                });
                if (result?.data?.isSuccessStatus) {
                    const surveys = result.data.data ?? [];
                    setSurveyList(surveys);
                    // Lưu vào store toàn cục để LeftSidebar hiển thị badge
                    // và SurveyManagement không cần fetch lại khi mở trang
                    setSurveyData(surveys);
                    setIsShowModal(surveys.length > 0);
                } else {
                    console.error('CheckESurvey Error:', result?.data?.errors);
                }
            }
        },
        [authData, setSurveyData]
    );

    useEffect(() => {
        if (!authData?.UserName) return;

        // Khách vãng lai: chỉ hiện 1 lần/session
        const hasClosedVisitorSurvey = sessionStorage.getItem(
            'visitor-pass-survey'
        );

        const extraInfo = authData.ExtraInfo;
        let type: UserSurveyType;

        if (extraInfo?.StaffInfo) {
            type = 'teacher';
        } else if (extraInfo?.StudentInfo) {
            type = 'student';
        } else {
            type = 'visitor';
            // Visitor đã bỏ qua trong session này → không hiện lại
            if (hasClosedVisitorSurvey === 'true') return;
        }

        // Tránh fetch trùng lặp cho cùng user trong cùng session
        if (fetchedForRef.current === authData.UserName) return;
        fetchedForRef.current = authData.UserName;

        setUserType(type);
        userRef.current = { type, id: authData.UserName };
        fetchSurvey(type, authData.UserName);
    }, [authData?.ExtraInfo, authData?.UserName, fetchSurvey]);

    // Khi sinh viên/cán bộ quay lại tab → refetch để kiểm tra đã làm xong chưa
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (
                document.visibilityState === 'visible' &&
                isShowModal &&
                userRef.current &&
                userRef.current.type !== 'visitor'
            ) {
                const { type, id } = userRef.current;
                fetchSurvey(type, id);
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
        };
    }, [fetchSurvey, isShowModal]);

    const isVisitor = userType === 'visitor';

    /** Mở tab mới để thực hiện khảo sát */
    const onOpenSurvey = useCallback(
        (survey: PSCCheckESurveyResponse) => {
            if (survey.Link) {
                window.open(survey.Link, '_blank', 'noopener,noreferrer');
            }
            // Visitor mở 1 khảo sát → coi như đã tương tác, đóng popup
            if (isVisitor) {
                sessionStorage.setItem('visitor-pass-survey', 'true');
                setIsShowModal(false);
            }
        },
        [isVisitor]
    );

    const onVisitorClose = useCallback(() => {
        sessionStorage.setItem('visitor-pass-survey', 'true');
        setIsShowModal(false);
    }, []);

    return (
        <Dialog open={isShowModal}>
            <DialogContent className='rounded-md max-lg:max-w-[95vw] max-lg:p-4 lg:top-[5%] lg:max-w-2xl lg:-translate-y-0 [&>button]:hidden'>
                <DialogHeader>
                    <DialogTitle className='text-xl'>
                        {isVisitor
                            ? transDef('System_Survey_Visitor')
                            : transDef(
                                  'System_Survey_YouNeedCompletedSurveyBeforeUseService'
                              )}
                    </DialogTitle>
                    <DialogDescription className='text-[13px] text-muted-foreground'>
                        {isVisitor
                            ? transDef('System_Survey_VisitorDesc')
                            : transDef('System_Survey_StudentStaffDesc')}
                    </DialogDescription>
                </DialogHeader>

                <Table className='text-gray-900'>
                    <TableHeader>
                        <TableRow className='cursor-default [&>*]:font-semibold [&>*]:text-gray-900'>
                            <TableHead className='w-12'>
                                {transDef('System_Survey_Order')}
                            </TableHead>
                            <TableHead>
                                {transDef('System_Survey_SurveyType')}
                            </TableHead>
                            <TableHead className='w-32' />
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {surveyList.map((survey, idx) => (
                            <TableRow key={idx}>
                                <TableCell className='text-center'>
                                    {idx + 1}
                                </TableCell>
                                <TableCell>
                                    {survey.name || survey.ClassName}
                                </TableCell>
                                <TableCell className='text-center'>
                                    {survey.Link && (
                                        <button
                                            tabIndex={-1}
                                            type='button'
                                            className='flex items-center justify-center gap-1 font-medium text-primary underline hover:opacity-90'
                                            onClick={() => onOpenSurvey(survey)}
                                        >
                                            <FileChartPie
                                                size={14}
                                                strokeWidth={2}
                                            />
                                            <span>
                                                {transDef('System_Survey')}
                                            </span>
                                        </button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                {/* Visitor có thể đóng popup; sinh viên/cán bộ không có nút đóng */}
                {isVisitor && (
                    <div className='mt-2 flex items-center justify-center gap-2 lg:justify-end'>
                        <CustomButton.Root
                            variant='outline'
                            onClick={onVisitorClose}
                            tabIndex={-1}
                            className='max-lg:w-full'
                        >
                            {transDef('System_Close')}
                        </CustomButton.Root>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
});

CheckSurveyOnline.displayName = 'CheckSurveyOnline';
